import { Box, Button, Container, Heading, Stack, Text } from '@chakra-ui/react'
import type { ReactElement } from 'react'
import 'swiper/swiper-bundle.css'

import { makeServerProps } from '@/server/query'

import SessionQ from '@/queries/SessionQ'
import TopicFollowQ from '@/queries/TopicFollowQ'
import TopicQ from '@/queries/TopicQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { SQL } from '@/client/query'
import { RPCReload } from '@/client/rpc'
import makePage from '@/client/services/pages/makePage'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import Layout from '@/components/Layout/Layout'
import PublicImage from '@/components/PublicImage/PublicImage'
import SessionCarousel from '@/components/Session/SessionCarousel/SessionCarousel'
import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import LinkableDiv from '@/components/UI/LinkableDiv'

const howItWorks = [
  {
    title: 'Join a Study Space',
    description: () => (
      <Text>Join a virtual study room with fellow learners across the world.</Text>
    ),
  },
  {
    title: 'Stay focused',
    description: () => (
      <Text>
        While you study or do homework together, keep each other focused– videos on or off.
      </Text>
    ),
  },
  {
    title: 'Track your study hours & reach your goals',
    description: () => (
      <Text>
        The amount of time you spend in Study Spaces is automatically tracked, so you can keep
        yourself accountable to reach your goals!
      </Text>
    ),
  },
]

function StudySpaces(): ReactElement {
  const user = useUser()
  const query = useQuery()

  const topicIsStudySpaces = SQL`${TopicQ.name} = 'Study Spaces'`

  const isFollowingStudySpaces: boolean =
    query`EXISTS (SELECT 1 ${TopicFollowQ.where(
      { userId: user.id, include: [{ association: 'Topic', required: true }] },
      topicIsStudySpaces
    )})` ?? false

  const toast = useToaster()
  const studySpacesTopic = query.one(TopicQ.where({}, topicIsStudySpaces))
  const studySpacesTopicId = studySpacesTopic(TopicQ.id) ?? 0
  const studySpacesSessions = query.many(
    SessionQ.where(
      {
        include: [{ association: 'Topic', required: true }],
        order: [[SessionQ.start_date, 'asc']],
      },
      SQL`${topicIsStudySpaces}
        AND ${SessionQ.getComputedEndDate()} > NOW()
        AND ${SessionQ.userCanViewSession(user.id)}`
    )
  )

  const [followStudySpacesCallback] = useAPIWrapper(
    async () => {
      await RPCReload.followTopic({
        topicId: studySpacesTopicId,
      })

      toast({
        title: 'You have successfully followed Study Spaces!',
        status: 'success',
      })
    },
    [studySpacesTopicId, toast],
    'Follow Study Spaces'
  )

  return (
    <Layout title="Study Spaces">
      <Box mt={8}>
        <Container centerContent maxWidth="800px">
          <Text variant="eyebrow">The wait is over</Text>
          <Box alignItems="center">
            <Heading size="3xl" as="h1" mt={4}>
              Studying’s better with buddies.
            </Heading>
          </Box>
          <Text align="center" mt={4} fontSize="20px" lineHeight="28px">
            Reach your goals together, with the Schoolhouse community, in <b>Study Spaces</b>.
          </Text>
          <ShwLink href={{ pathname: '/study-spaces', hash: 'upcoming' }} shallow>
            <Button mt={6} variant="solid">
              Join a Study Space
            </Button>
          </ShwLink>
        </Container>
      </Box>
      <Box mt={8} mb={8}>
        <Container centerContent maxWidth="800px">
          <PublicImage
            src="/study-spaces/StudySpaces.png"
            alt="Reach your goals, together with the Schoolhouse community in Study Spaces."
            maxWidth="100%"
          />
        </Container>
      </Box>

      <LinkableDiv id="upcoming">
        <Box pt={4} bgColor="bg.subtle">
          <Container centerContent>
            <Text variant="eyebrow" alignSelf="center">
              Upcoming Study Spaces
            </Text>
          </Container>
        </Box>
      </LinkableDiv>
      {studySpacesSessions.length > 0 ? (
        <Box mt={0} pt={0} bgColor="bg.subtle">
          <Container>
            <Box>
              <SessionCarousel sessions={studySpacesSessions} />
            </Box>
          </Container>
        </Box>
      ) : null}
      {studySpacesSessions.length > 0 ? null : (
        <Box mt={0} bgColor="bg.subtle">
          <Container centerContent>
            <Text mb={10} color="fg.subtle">
              No upcoming Study Spaces at this time.{' '}
              {isFollowingStudySpaces
                ? `Since you're following the topic, you'll be notified when there are more available!`
                : 'Click below to get notified when there are more available!'}
            </Text>
            {isFollowingStudySpaces ? null : (
              <RequireLoginAndApproval>
                <Button onClick={followStudySpacesCallback}>Follow Study Spaces</Button>
              </RequireLoginAndApproval>
            )}
          </Container>
        </Box>
      )}
      <Box mt={8} mb={16}>
        <Container centerContent>
          <Text variant="eyebrow" mb={12}>
            How it Works
          </Text>
          <Stack direction="column" maxWidth="800px" spacing="48px" px="16px">
            {howItWorks.map(({ title, description }, index) => (
              <Stack key={title} direction="row" spacing={8} align="left">
                <Heading fontSize="80px" color="gray.muted" fontWeight={900}>
                  {index + 1}
                </Heading>
                <Box>
                  <Heading size="lg" as="h4" mb="16px">
                    {title}
                  </Heading>
                  <Text>{description()}</Text>
                </Box>
              </Stack>
            ))}
          </Stack>{' '}
        </Container>
      </Box>
    </Layout>
  )
}

const StudySpacesPage = makePage(StudySpaces, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(StudySpacesPage)
export default StudySpacesPage
